import {
  TeamOutlined,
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Input,
  Popconfirm,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
  theme,
} from 'antd';
import axios from 'axios';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';
import { CardHeader, PageHeader } from 'components/headings';
import { ImportExportToolbar } from 'components/global/ImportExport';
import { CLIENTE_COLUMNS } from 'components/global/ImportExport/clienteColumns';
import useTenantFlags from 'hooks/useTenantFlags';
import { useClientesActions } from 'providers/Clientes/ClientesActionsProvider';
import { useClientesComplianceBatch } from 'providers/Compliance';
import React, { useState, useMemo, useEffect } from 'react';
import ClienteRowActions from './Clientes/ClienteRowActions';
import HealthRing from './Clientes/HealthRing';
import paths from 'assets/routes/paths';
import { useNavigate } from 'react-router-dom';
import { downloadFile } from 'utils/downloadFile';

const { Text } = Typography;
const { useToken } = theme;

const CLIENTE_IE_CONFIG = {
  exportUrl: (scope, format) => `/clientes/export?format=${format}`,
  validateUrl: '/clientes/import/validate',
  commitUrl: '/clientes/import/commit',
};

// Paleta estable para los avatares (ADN de marca del Diseño 2).
const AVATAR_COLORS = ['#0055b3', '#1b4f72', '#2e6b3a', '#b8861a', '#c5561d', '#6d4ca0', '#0e7c86'];

const initial = (name) => (name || 'C').trim().charAt(0).toUpperCase();

// Switch de preventiva automática por fila. Estado local optimista (toggleAutoPreven
// es debounced y maneja su propio modal/errores), seedeado de la data del cliente.
const PrevenSwitch = ({ cliente, disabled, onToggle }) => {
  const [checked, setChecked] = useState(!!cliente.automatizadoManPreven);
  const handle = (next) => {
    const ascCount = cliente.Ascensores?.length ?? cliente.nAscensores ?? 0;
    if (next && ascCount === 0) {
      notification.error({
        message: 'Para activar la preventiva automática el cliente debe tener al menos un ascensor',
      });
      return;
    }
    setChecked(next);
    onToggle(cliente.id, next);
  };
  return (
    <Switch
      checked={checked}
      onChange={handle}
      disabled={disabled}
      checkedChildren="Auto"
      unCheckedChildren="Off"
    />
  );
};

const ClientList = (props) => {
  const {
    clients,
    isLoading,
    openEditClient,
    openDeleteClient,
    getClientProblems,
    openAddClient,
    refresh,
  } = props;

  const { token } = useToken();
  const navigate = useNavigate();
  const { readOnly, minvuEnabled } = useTenantFlags();
  const { toggleStatus, toggleAutoPreven } = useClientesActions();
  const { dataByCliente: complianceByCliente, fetchBatch: fetchComplianceBatch } =
    useClientesComplianceBatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');
  const [exportingCompleto, setExportingCompleto] = useState(false);

  const handleExportCompleto = async () => {
    setExportingCompleto(true);
    try {
      const res = await axios.get(`${API_PATH}/clientes/export-completo?format=xlsx`, {
        headers: { Authorization: getAccessTokenApi() },
        responseType: 'blob',
      });
      downloadFile(res.data, 'clientes-completo.xlsx');
    } catch (e) {
      notification.error({
        message: 'No se pudo generar el reporte completo',
        description: e?.response?.data?.message || e.message,
      });
    } finally {
      setExportingCompleto(false);
    }
  };

  // Un solo POST a /compliance/batch para el semáforo de cada cliente.
  useEffect(() => {
    if (Array.isArray(clients) && clients.length > 0) {
      const ids = clients.map((c) => c.id).filter((n) => Number.isFinite(n));
      fetchComplianceBatch(ids);
    }
  }, [clients, fetchComplianceBatch]);

  const handleClientStatus = async (client) => {
    await toggleStatus(client.id, client.active);
    refresh();
  };

  const filteredClients = useMemo(() => {
    let filtered = clients || [];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (client) =>
          client.name?.toLowerCase().includes(q) ||
          client.email?.toLowerCase().includes(q) ||
          client.rut?.toLowerCase().includes(q)
      );
    }
    if (filter === 'avisos') {
      filtered = filtered.filter((client) => client.problems);
    }
    return filtered;
  }, [clients, searchTerm, filter]);

  const stats = useMemo(() => {
    const total = clients?.length || 0;
    const active = clients?.filter((c) => c.active).length || 0;
    const ascensores = clients?.reduce((acc, c) => acc + (c.Ascensores?.length || 0), 0) || 0;
    return { total, active, inactive: total - active, ascensores };
  }, [clients]);

  const statTiles = [
    { v: stats.total, l: 'Total Clientes', icon: TeamOutlined, tone: token.colorPrimary },
    { v: stats.active, l: 'Activos', icon: CheckCircleOutlined, tone: token.colorSuccess },
    { v: stats.inactive, l: 'Inactivos', icon: CloseCircleOutlined, tone: token.colorTextTertiary },
    { v: stats.ascensores, l: 'Ascensores', icon: SettingOutlined, tone: token.colorInfo },
  ];

  const panelStyle = {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    overflow: 'hidden',
    background: token.colorBgContainer,
    boxShadow: token.boxShadowSecondary,
  };

  const columns = [
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, c, idx) => (
        <div
          onClick={() => navigate(paths.admin.clientes.detail(c.id))}
          style={{ cursor: 'pointer' }}
          title="Ver datos del cliente"
        >
          <Space size={11} style={{ minWidth: 0 }}>
            <Avatar
              shape="square"
              size={38}
              style={{
                backgroundColor: AVATAR_COLORS[idx % AVATAR_COLORS.length],
                fontWeight: 700,
                borderRadius: 9,
                flex: '0 0 auto',
              }}
            >
              {initial(c.name)}
            </Avatar>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: token.colorText, lineHeight: 1.3 }}>
                {c.name || 'Sin nombre'}
              </div>
              <div
                style={{
                  fontFamily: token.fontFamilyCode,
                  fontSize: 11,
                  color: token.colorTextTertiary,
                }}
              >
                {c.rut || '—'}
              </div>
            </div>
          </Space>
        </div>
      ),
    },
    {
      title: 'Contacto',
      key: 'contacto',
      render: (_, c) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Text
            type="secondary"
            style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 7 }}
          >
            <MailOutlined style={{ color: token.colorTextQuaternary }} />
            {c.email || '—'}
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 7 }}
          >
            <PhoneOutlined style={{ color: token.colorTextQuaternary }} />
            {c.phone || '—'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Ascensores',
      key: 'ascensores',
      align: 'center',
      sorter: (a, b) => (a.Ascensores?.length || 0) - (b.Ascensores?.length || 0),
      render: (_, c) => (
        <span
          style={{
            fontFamily: token.fontFamilyCode,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
          }}
        >
          <SettingOutlined style={{ color: token.colorTextQuaternary }} />
          {c.Ascensores?.length || 0}
        </span>
      ),
    },
    minvuEnabled && {
      title: 'Mantención',
      key: 'mantencion',
      render: (_, c) => <HealthRing score={complianceByCliente[c.id]?.score} />,
    },
    {
      title: 'Avisos',
      key: 'avisos',
      align: 'center',
      render: (_, c) =>
        c.problems ? (
          <Tag
            color="warning"
            icon={<ExclamationCircleOutlined />}
            style={{ margin: 0, cursor: 'pointer' }}
            onClick={() => getClientProblems(c)}
          >
            Avisos
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Estado',
      key: 'estado',
      align: 'center',
      filters: [
        { text: 'Activo', value: true },
        { text: 'Inactivo', value: false },
      ],
      onFilter: (value, c) => !!c.active === value,
      render: (_, c) =>
        c.active ? (
          <Popconfirm
            title="¿Desactivar este cliente?"
            okText="Sí"
            cancelText="No"
            onConfirm={() => handleClientStatus(c)}
            disabled={readOnly}
          >
            <Switch
              checked
              checkedChildren="Activo"
              unCheckedChildren="Inactivo"
              disabled={readOnly}
            />
          </Popconfirm>
        ) : (
          <Switch
            checked={false}
            onChange={() => handleClientStatus(c)}
            checkedChildren="Activo"
            unCheckedChildren="Inactivo"
            disabled={readOnly}
          />
        ),
    },
    {
      title: 'Preventiva',
      key: 'preventiva',
      align: 'center',
      render: (_, c) => (
        <PrevenSwitch cliente={c} disabled={readOnly} onToggle={toggleAutoPreven} />
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      width: 56,
      render: (_, c) => (
        <ClienteRowActions
          usuario={c}
          openEditClient={openEditClient}
          openDeleteClient={openDeleteClient}
          getClientProblems={getClientProblems}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginLG,
        paddingTop: token.paddingMD,
        paddingBottom: token.paddingXL,
      }}
    >
      <PageHeader
        icon={TeamOutlined}
        title="Gestión de Clientes"
        subtitle="Administra y visualiza todos los clientes del sistema"
        actions={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={isLoading}>
              Actualizar
            </Button>
            {!readOnly && (
              <ImportExportToolbar
                scope={{ level: 'global' }}
                columns={CLIENTE_COLUMNS}
                config={CLIENTE_IE_CONFIG}
                humanName="clientes"
                onCommitSuccess={refresh}
              />
            )}
            {!readOnly && (
              <Button
                icon={<FileExcelOutlined />}
                loading={exportingCompleto}
                onClick={handleExportCompleto}
              >
                Exportar completo
              </Button>
            )}
            {!readOnly && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openAddClient}>
                Agregar Cliente
              </Button>
            )}
          </Space>
        }
      />

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gap: token.marginMD,
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        }}
      >
        {statTiles.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.l}
              style={{
                ...panelStyle,
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: token.paddingMD,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: token.borderRadius,
                  background: `${s.tone}1a`,
                  color: s.tone,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 19,
                  flex: '0 0 auto',
                }}
              >
                <Icon />
              </div>
              <div>
                <div
                  style={{
                    fontFamily: token.fontFamilyCode,
                    fontSize: 22,
                    fontWeight: 700,
                    lineHeight: 1.1,
                  }}
                >
                  {s.v}
                </div>
                <Text type="secondary" style={{ fontSize: 12.5 }}>
                  {s.l}
                </Text>
              </div>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar por nombre, RUT o email…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: 340, maxWidth: '100%' }}
        />
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { label: 'Todos', value: 'todos' },
            { label: 'Con avisos', value: 'avisos' },
          ]}
        />
        <div style={{ flex: 1 }} />
        <Tag style={{ margin: 0, fontWeight: 600 }}>
          {filteredClients.length} de {stats.total} clientes
        </Tag>
      </div>

      {/* Tabla */}
      <div style={panelStyle}>
        <CardHeader
          icon={TeamOutlined}
          tint={token.colorSuccess}
          title="Lista de clientes"
          subtitle="Todos los clientes del sistema y su estado de mantención"
        />
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={filteredClients}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 12,
            showSizeChanger: true,
            pageSizeOptions: ['6', '12', '24', '48'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} clientes`,
          }}
        />
      </div>
    </div>
  );
};

export default ClientList;
